import axios from 'axios';
import moment from 'moment';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Loader from 'components/Base/Loader';
import { countdown } from 'utils/helpers';
import { useStore } from 'store';
import { observer } from 'mobx-react-lite';

const VISIT_REASONS = [
  { id: 'grief', label: 'Coping with a loss or grief' },
  { id: 'lonely', label: 'Feeling lonely or isolated' },
  { id: 'health', label: 'Changes in health or mobility' },
  { id: 'anxious', label: 'Feeling anxious or worried' },
  { id: 'talk', label: 'Just need someone to talk to' },
  { id: 'other', label: 'Other (please specify)' },
];

const AthenaPatientInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chosenSlot = location.state?.chosenSlot;

  useEffect(() => {
    if (!chosenSlot) {
      navigate('/athena/welcome');
    }
  }, [chosenSlot, navigate]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [isAddressVerifying, setIsAddressVerifying] = useState(false);
  const [isAddressVerified, setIsAddressVerified] = useState(false);

  const [originalPhone, setOriginalPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [isCodeSent, setIsCodeSent] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    isPhoneVerified: false,
    homeAddress: '',
    city: '',
    state: '',
    zipCode: '',
    medicareId: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    homeAccessInstructions: '',
    reasonsForVisit: [],
    otherReason: '',
  });

  const [errors, setErrors] = useState({});

  const store = useStore();

  useEffect(() => {
    axios.get('/profile/general')
      .then((response) => {
        const data = response.data || {};

        const fName = data.firstName || '';
        const lName = data.lastName || '';
        const ph = data.phone || '';
        const zip = data.zipCode || data.location?.zipCode || '';
        const cy = data.city || data.location?.city || '';
        const st = data.state || data.location?.state || '';
        const home = data.homeAddress || data.address || data.location?.homeAddress || '';

        setOriginalPhone(ph);

        setFormData((prev) => ({
          ...prev,
          firstName: fName,
          lastName: lName,
          phone: ph,
          zipCode: zip,
          city: cy,
          state: st,
          homeAddress: home,
          isPhoneVerified: !!ph
        }));
      })
      .catch((error) => {
        console.error('Error fetching general profile:', error);
      });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    const addressFields = ['homeAddress', 'city', 'state', 'zipCode'];
    if (addressFields.includes(name)) {
      setIsAddressVerified(false);
    }

    if (name === 'phone') {
      setFormData(prev => ({
        ...prev,
        isPhoneVerified: value === originalPhone && value !== ''
      }));
      setIsCodeSent(false);
      setPhoneCode('');
    }
  };

  const handleReasonChange = (reasonId) => {
    setFormData((prev) => {
      const updated = prev.reasonsForVisit.includes(reasonId)
        ? prev.reasonsForVisit.filter((r) => r !== reasonId)
        : [...prev.reasonsForVisit, reasonId];
      return { ...prev, reasonsForVisit: updated };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.homeAddress.trim()) newErrors.homeAddress = 'Home address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'Zip code is required';
    if (!formData.medicareId.trim()) newErrors.medicareId = 'Medicare ID is required';
    if (formData.medicareId.trim().length !== 11) newErrors.medicareId = 'Medicare ID must be 11 characters';
    if (formData.reasonsForVisit.length === 0) newErrors.reasonsForVisit = 'Please select at least one reason for your visit';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const verifyAddress = async () => {
    if (!formData.homeAddress || !formData.city || !formData.state || !formData.zipCode) {
      setError("Please fill in all address fields before verifying.");
      return;
    }

    setIsAddressVerifying(true);
    setError('');
    const addressPayload = {
      addressLine1: formData.homeAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
    };

    try {
      const { data } = await axios.patch('/profile/verify/address', { addressPayload }, { timeout: 30000 });
      setIsAddressVerified(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to verify address';
      setError(errorMessage);
      setIsAddressVerified(false);
    } finally {
      setIsAddressVerifying(false);
    }
  };

  const validatePhoneNumber = async ({ resend }) => {
    try {
      setError('');
      await axios.post("/auth/signup/number/check", {
        resend,
        number: formData.phone
      });

      setTimeLeft(60);
      countdown(setTimeLeft);
      setIsCodeSent(true);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send OTP. Please try again.');
    }
  };

  const confirmPhoneValidateCode = async () => {
    try {
      setError('');
      await axios.post('/auth/signup/number/confirm', {
        code: phoneCode,
        number: formData.phone
      });

      setIsCodeSent(false);
      setTimeLeft(0);

      setFormData(prevForm => ({ ...prevForm, isPhoneVerified: true }));
      setOriginalPhone(formData.phone);
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid code. Please try again.');
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) return;

      setLoading(true);
      setError('');

      try {
        const payload = {
          name: formData.firstName,
          surname: formData.lastName,
          dob: formData.dateOfBirth,
          phone: formData.phone,
          location: {
            addressLine1: formData.homeAddress,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
          },
          emergencyContact: {
            name: formData.emergencyContactName,
            relationship: formData.emergencyContactRelationship,
            phone: formData.emergencyContactPhone,
          },
          reasonsForVisit: formData.reasonsForVisit,
          otherReason: formData.otherReason,
          homeAccessInstructions: formData.homeAccessInstructions,
          notes: [
            {
              chosenSlot,
              appointmentDate: chosenSlot.date,
              appointmentTime: chosenSlot.starttime,
              appointmentId: chosenSlot.appointmentid,

            },
            {
              medicareId: formData.medicareId,
            }
          ],
        };

        await axios.patch('/profile/complete', payload)
        store.auth.user.isCompleteProfile = true;
        setSubmitted(true);
        setTimeout(() => {
          navigate('/jobs/athena');
        }, 5000);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to submit appointment request.');
        setLoading(false);
      }
    },
    [formData, chosenSlot, navigate]
  );

  const isFormReadyToSubmit = isAddressVerified && formData.isPhoneVerified;

  if (submitted) {
    return (
      <div className="page p-5 max-w-4xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-green-600 mb-2">Your request is in!</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            We are securely verifying your Medicare benefits to ensure you have no surprise bills. We will call or email you within 48
            hours to confirm your visit for <strong>{moment(chosenSlot?.date, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('MMMM D, YYYY')}</strong> at <strong>{moment(chosenSlot?.starttime, ['HH:mm', 'hh:mm A']).format('hh:mm A')}</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page p-5 max-w-4xl mx-auto">
      <h1 className="text-4xl font-bold mb-2 text-gray-800">Complete Your Profile</h1>
      <p className="text-gray-600 mb-6">
        We need your information to confirm your appointment for{' '}
        <span className="font-semibold">{moment(chosenSlot.date, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('MMMM D, YYYY')}</span> at{' '}
        <span className="font-semibold">{moment(chosenSlot.starttime, ['HH:mm', 'hh:mm A']).format('hh:mm A')}</span>
      </p>

      <Loader show={loading} />
      {error && <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Personal Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input id="firstName" type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className={classNames('w-full px-3 py-2 border rounded-lg', errors.firstName ? 'border-red-500' : 'border-gray-300')} />
              {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input id="lastName" type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className={classNames('w-full px-3 py-2 border rounded-lg', errors.lastName ? 'border-red-500' : 'border-gray-300')} />
              {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
              <input id="dateOfBirth" type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} className={classNames('w-full px-3 py-2 border rounded-lg', errors.dateOfBirth ? 'border-red-500' : 'border-gray-300')} />
              {errors.dateOfBirth && <p className="text-xs text-red-600 mt-1">{errors.dateOfBirth}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                className={classNames('w-full px-3 py-2 border rounded-lg', errors.phone ? 'border-red-500' : 'border-gray-300')}
              />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}

              {formData.isPhoneVerified && formData.phone && (
                <p className="text-xs text-green-600 mt-1 font-medium">✓ Phone verified</p>
              )}

              {formData.phone && !formData.isPhoneVerified && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm">
                  {!isCodeSent ? (
                    <div>
                      <p className="text-xs text-yellow-800 mb-2 font-medium">New number detected. Please verify to continue.</p>
                      <button
                        type="button"
                        onClick={() => validatePhoneNumber({ resend: false })}
                        className="text-xs px-4 py-2 bg-yellow-500 text-white font-semibold rounded hover:bg-yellow-600 transition"
                      >
                        Send Verification Code
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <p className="text-xs text-yellow-800 font-medium">
                        Code sent! {timeLeft > 0 ? <span className="text-gray-500">(Resend in {timeLeft}s)</span> : ''}
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={phoneCode}
                          onChange={(e) => setPhoneCode(e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={confirmPhoneValidateCode}
                          className="text-sm px-4 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 transition"
                        >
                          Confirm
                        </button>
                      </div>
                      {timeLeft === 0 && (
                        <button
                          type="button"
                          onClick={() => validatePhoneNumber({ resend: true })}
                          className="text-xs text-blue-600 text-left hover:underline font-medium"
                        >
                          Resend Code
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Home Address</h2>
          <div className="mb-4">
            <label htmlFor="homeAddress" className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
            <input id="homeAddress" type="text" name="homeAddress" value={formData.homeAddress} onChange={handleInputChange} className={classNames('w-full px-3 py-2 border rounded-lg', errors.homeAddress ? 'border-red-500' : 'border-gray-300')} />
            {errors.homeAddress && <p className="text-xs text-red-600 mt-1">{errors.homeAddress}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input id="city" type="text" name="city" value={formData.city} onChange={handleInputChange} className={classNames('w-full px-3 py-2 border rounded-lg', errors.city ? 'border-red-500' : 'border-gray-300')} />
              {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">State *</label>
              <input id="state" type="text" name="state" value={formData.state} onChange={handleInputChange} maxLength="2" className={classNames('w-full px-3 py-2 border rounded-lg', errors.state ? 'border-red-500' : 'border-gray-300')} />
              {errors.state && <p className="text-xs text-red-600 mt-1">{errors.state}</p>}
            </div>
            <div>
              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">Zip Code *</label>
              <input id="zipCode" type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} className={classNames('w-full px-3 py-2 border rounded-lg', errors.zipCode ? 'border-red-500' : 'border-gray-300')} />
              {errors.zipCode && <p className="text-xs text-red-600 mt-1">{errors.zipCode}</p>}
            </div>

            <div className="mb-6 col-span-1 sm:col-span-3">
              <button
                type="button"
                onClick={verifyAddress}
                disabled={isAddressVerifying || loading || isAddressVerified}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors border ${isAddressVerified
                  ? 'bg-green-50 text-green-700 border-green-300 cursor-default'
                  : isAddressVerifying || loading
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                  }`}
              >
                {isAddressVerified ? '✓ Address Verified' : isAddressVerifying ? 'Verifying...' : 'Verify Address'}
              </button>
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Medicare Information</h2>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 md:p-6 mb-4 flex flex-col xl:flex-row gap-8 items-center shadow-sm overflow-hidden">

            <div className="flex-1 w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Find your Medicare Number</h3>
              <p className="text-sm text-gray-600 mb-6">
                Please enter the 11-character Medicare Number (MBI) exactly as it appears on your card. It contains numbers and uppercase letters.
              </p>

              <div>
                <label htmlFor="medicareId" className="block text-sm font-medium text-gray-700 mb-1">
                  Medicare ID (MBI) *
                </label>
                <input
                  id="medicareId"
                  type="text"
                  name="medicareId"
                  value={formData.medicareId}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    if (val.length <= 11) {
                      handleInputChange({ target: { name: 'medicareId', value: val } });
                    }
                  }}
                  placeholder="1EG4TE5MK72"
                  className={classNames(
                    'w-full max-w-sm px-4 py-3 border rounded-lg font-mono text-xl tracking-widest focus:outline-none focus:ring-2 transition-shadow bg-white',
                    errors.medicareId ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  )}
                />
                {errors.medicareId && <p className="text-sm text-red-600 mt-2 font-medium">{errors.medicareId}</p>}
                <p className="text-xs text-gray-500 mt-2">
                  {formData.medicareId.length}/11 characters
                </p>
              </div>
            </div>

            <div className="flex-shrink-0 w-full max-w-[340px] relative bg-white border border-gray-300 shadow-md font-sans text-black overflow-hidden select-none rounded-md mx-auto xl:mx-0">

              <div className="bg-[#0b3b8c] text-white p-2 md:p-3 flex items-center gap-2 relative z-10">
                <svg className="w-6 h-6 fill-white opacity-90" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                </svg>
                <h2 className="text-sm md:text-base font-bold tracking-wide leading-tight">
                  MEDICARE HEALTH INSURANCE
                </h2>
              </div>

              <div className="p-3 md:p-4 relative z-10">
                <div className="mb-3">
                  <p className="text-[#0b3b8c] text-[9px] font-semibold leading-none mb-1">Name/Nombre</p>
                  <p className="text-sm md:text-base font-bold leading-none">JOHN L SMITH</p>
                </div>

                <div className="relative mb-4 inline-block w-full">

                  <div className="absolute -left-2 right-24 top-2 -bottom-1 border-[3px] border-[#a855f7] rounded-[100%] z-20 pointer-events-none"></div>

                  <p className="text-[#0b3b8c] text-[9px] font-semibold leading-none mb-1">
                    Medicare Number/Número de Medicare
                  </p>
                  <p className="text-lg md:text-xl font-bold tracking-widest relative z-10">
                    1EG4-TE5-MK72
                  </p>

                  <div className="absolute -right-6 md:-right-3 top-1/2 -translate-y-1/2 flex items-center z-20 pointer-events-none">
                    <div className="w-0 h-0 border-y-[8px] border-y-transparent border-r-[14px] border-r-[#a855f7]"></div>
                    <div className="w-16 md:w-20 h-[4px] bg-[#a855f7] -ml-1"></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div>
                    <p className="text-[#0b3b8c] text-[9px] font-semibold leading-none mb-1">Entitled to/Con derecho a</p>
                    <p className="text-xs md:text-sm font-bold uppercase leading-tight">PART A</p>
                    <p className="text-xs md:text-sm font-bold uppercase leading-tight mt-1">PART B</p>
                  </div>
                  <div>
                    <p className="text-[#0b3b8c] text-[9px] font-semibold leading-none mb-1">Coverage starts/Cobertura empieza</p>
                    <p className="text-xs md:text-sm font-bold leading-tight">03-03-2016</p>
                    <p className="text-xs md:text-sm font-bold leading-tight mt-1">03-03-2016</p>
                  </div>
                </div>
              </div>

              <div className="h-[4px] md:h-[6px] bg-[#d32f2f] relative z-10 mt-1"></div>
            </div>

          </div>
        </div>

        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Emergency Contact <span className="text-sm text-gray-500 font-normal">(Optional)</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="emergencyContactName" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input id="emergencyContactName" type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label htmlFor="emergencyContactRelationship" className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
              <input id="emergencyContactRelationship" type="text" name="emergencyContactRelationship" value={formData.emergencyContactRelationship} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label htmlFor="emergencyContactPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input id="emergencyContactPhone" type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Reason for Visit *</h2>
          <div className="space-y-3 mb-4">
            <div className="space-y-3 mb-4">
              {VISIT_REASONS.map((reason) => (
                <div key={reason.id} className="flex flex-col">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.reasonsForVisit.includes(reason.id)}
                      onChange={() => handleReasonChange(reason.id)}
                      className="mt-1 w-4 h-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{reason.label}</span>
                  </label>

                  {reason.id === 'other' && formData.reasonsForVisit.includes('other') && (
                    <div className="ml-7 mt-2">
                      <input
                        type="text"
                        name="otherReason"
                        value={formData.otherReason}
                        onChange={handleInputChange}
                        placeholder="Please type your reason here..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {errors.reasonsForVisit && <p className="text-xs text-red-600 mt-1">{errors.reasonsForVisit}</p>}
        </div>

        <div className="border-t pt-6 pb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Home Access Instructions <span className="text-sm text-gray-500 font-normal">(Optional)</span></h2>
          <textarea id="homeAccessInstructions" name="homeAccessInstructions" value={formData.homeAccessInstructions} onChange={handleInputChange} rows="4" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="Gate codes, parking instructions, etc." />
        </div>

        <div className="border-t pt-6 flex flex-col sm:flex-row items-center justify-end gap-4">

          {!isFormReadyToSubmit && (
            <span className="text-sm text-red-500 font-medium text-right">
              {!isAddressVerified && !formData.isPhoneVerified
                ? '* Please verify your Address and Phone to continue'
                : !isAddressVerified
                  ? '* Please verify your Address to continue'
                  : '* Please verify your Phone to continue'
              }
            </span>
          )}

          <div className="flex gap-4 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => navigate('/jobs/create/athena/welcome')}
              className="w-full sm:w-auto px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition"
            >
              Back
            </button>

            { }            <button
              type="submit"
              disabled={loading || !isFormReadyToSubmit}
              className="w-full sm:w-auto px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default observer(AthenaPatientInfo);